import noAttributeImage from '@components-react/ProductTile/assets/no-attribute-image.svg';
import type { CodeValue, Product, Variant } from '@sctypes/product/Product';
import type { AttributeList } from '@sctypes/product/AttributeList';
import type { Attribute } from '@sctypes/product/Attribute';

export interface VariantOption {
  typeCode: string;
  codeValue: CodeValue;
  value: CodeValue;
  disabled: boolean;
  image: string;
  selected?: boolean;
}

function mapVariantOptions(options: AttributeList): VariantOption[] {
  const typeCode = options.type.code;
  return options.items.map((item: Attribute) => {
    return {
      typeCode: typeCode,
      codeValue: item?.code || { code: '', value: '' },
      value: item?.value || { code: '', value: '' },
      disabled: false,
      image: (item.images?.length > 0 ? item.images[0].src : noAttributeImage)
    };
  });
}

export interface ProductData extends Product {
  availableOptionCodes1: VariantOption[];
  availableOptionCodes2: VariantOption[];
  variant: Variant;
  optionCode1: string;
  optionCode2: string;
  unitOfMeasureCode: string;
}

export function mapProductData(product: Product, chosenVariant?: Variant): ProductData {
  if (!product) {
    return null;
  }

  const { variants, variantOptions } = product;

  let variant = chosenVariant;
  const options1: VariantOption[] = variantOptions[0]?.items ? mapVariantOptions(variantOptions[0]) : [];
  const options2: VariantOption[] = variantOptions[1]?.items ? mapVariantOptions(variantOptions[1]) : [];

  let optionCode1 = '';
  let optionCode2 = '';
  if (chosenVariant) {
    optionCode1 = chosenVariant.optionCode1;
    optionCode2 = chosenVariant.optionCode2;
  } else {
    variant = variants.find(variant => variant.code === product.defaultVariantCode);

    if (variant) {
      optionCode1 = variant.optionCode1;
      optionCode2 = variant.optionCode2;
    }
  }

  if (optionCode1 !== '') {
    for (let index = 0; index < options2.length; index++) {
      const optionCode = options2[index].value.code;
      const hasVariant = variants.find(variant => variant.optionCode1 === optionCode1 && variant.optionCode2 === optionCode);

      if (!hasVariant) {
        options2[index].disabled = true;
      }
    }
  }

  return {
    ...product,
    availableOptionCodes1: options1,
    availableOptionCodes2: options2,
    variant,
    optionCode1,
    optionCode2,
    unitOfMeasureCode: variant?.unitsOfMeasure[0].code
  };
}

/**
 * Maps URL path aliases to their attribute typeCode.
 * Mirrors C# ProductsConstants.AliasMap — extend here when new attribute types are added.
 */
const URL_ALIAS_TO_TYPE_CODE: Record<string, string> = {
  kleur: '1400',
  maat: '1300',
  materiaal: '1500',
  merk: '1100',
  toepassing: '1000'
};

/** { [typeCode]: string[] } — e.g. { "1400": ["w"], "1300": ["39", "42"] } */
export type UrlFacets = Record<string, string[]>;

/**
 * Parses URL path segments like `/c/werkkleding/kleur/w/maat/39/maat/42`
 * into a facet dictionary keyed by attribute typeCode.
 */
export function parseUrlFacets(pathname: string): UrlFacets {
  const segments = pathname.split('/').filter(Boolean);
  const facets: UrlFacets = {};

  for (let i = 0; i < segments.length - 1; i++) {
    const typeCode = URL_ALIAS_TO_TYPE_CODE[segments[i]];
    if (typeCode) {
      if (!facets[typeCode]) {
        facets[typeCode] = [];
      }
      facets[typeCode].push(segments[i + 1]);
      i++; // skip the value segment that was just consumed
    }
  }

  return facets;
}

/**
 * Returns the best-matching variant for a product given active URL facets.
 * Preference order:
 *   1. Variant matching both optionCode1 and optionCode2 facet values
 *   2. Variant matching only optionCode1 facet values
 *   3. Variant matching only optionCode2 facet values
 *   4. undefined (caller should fall back to defaultVariantCode)
 */
export function findVariantByUrlFacets(product: Product, facets: UrlFacets): Variant | undefined {
  const { variants, variantOptions } = product;

  if (!variants?.length || !variantOptions?.length || !Object.keys(facets).length) {
    return undefined;
  }

  const typeCode1 = variantOptions[0]?.type?.code;
  const typeCode2 = variantOptions[1]?.type?.code;

  const values1 = typeCode1 ? (facets[typeCode1] ?? []) : [];
  const values2 = typeCode2 ? (facets[typeCode2] ?? []) : [];

  if (values1.length > 0 && values2.length > 0) {
    for (const v1 of values1) {
      for (const v2 of values2) {
        const match = variants.find((variant) => variant.optionCode1 === v1 && variant.optionCode2 === v2);
        if (match) {
          return match;
        }
      }
    }
  }

  if (values1.length > 0) {
    for (const v1 of values1) {
      const match = variants.find((variant) => variant.optionCode1 === v1);
      if (match) {
        return match;
      }
    }
  }

  if (values2.length > 0) {
    for (const v2 of values2) {
      const match = variants.find((variant) => variant.optionCode2 === v2);
      if (match) {
        return match;
      }
    }
  }

  return undefined;
}
